/*
 * The "Patient insights" dashboard on the analytics screen. A synthetic lab's
 * patients for Jan–Dec 2024, filtered by profile. Each profile's patient count
 * is All × its share (38 / 24 / 18 / 20%), and the finding and age figures of
 * the four profiles average back to the All figures (share-weighted).
 * General health stays in the data (it is a fifth of All) but has no filter
 * chip: the client dropped that block (meeting 2026-10-20), so the four chips
 * fit on one row.
 */
import { useState } from 'react';
import { CalendarDays, TrendingUp, TriangleAlert, Users } from 'lucide-react';
import { BarsGlyph } from '../../mocks/ProductGlyphs';
import './AnalyticsDashboard.css';

const SCREEN = 'product.analytics';

export const FINDINGS = [
  { key: 'vitd', name: 'Vitamin D deficiency' },
  { key: 'iron', name: 'Iron deficiency anaemia' },
  { key: 'prediabetes', name: 'Prediabetes' },
  { key: 'thyroid', name: 'Thyroid (TSH)' },
  { key: 'ldl', name: 'High LDL cholesterol' },
];

const AGE_GROUPS = ['18–30', '31–45', '46–60', '60+'];

/* A finding counts as an opportunity when at least this share has it. */
const OPPORTUNITY_MIN = 25;

export const PROFILES = [
  {
    key: 'all',
    label: 'All patients',
    patients: 2523,
    abnormal: 46,
    reach: 27,
    findings: { vitd: 63, iron: 41, prediabetes: 28, thyroid: 22, ldl: 18 },
    ages: [12, 28, 38, 22],
  },
  {
    key: 'womens-wellness',
    label: "Women's wellness",
    share: 38,
    patients: 959,
    abnormal: 46,
    reach: 34,
    findings: { vitd: 71, iron: 66, prediabetes: 12, thyroid: 30, ldl: 7 },
    ages: [17, 38, 35, 10],
  },
  {
    key: 'diabetes',
    label: 'Diabetes care',
    share: 24,
    patients: 606,
    abnormal: 62,
    reach: 22,
    findings: { vitd: 58, iron: 24, prediabetes: 58, thyroid: 18, ldl: 26 },
    ages: [4, 19, 45, 32],
  },
  {
    key: 'cardiac',
    label: 'Cardiac care',
    share: 18,
    patients: 454,
    abnormal: 55,
    reach: 19,
    findings: { vitd: 55, iron: 21, prediabetes: 34, thyroid: 15, ldl: 38 },
    ages: [2, 12, 46, 40],
  },
  {
    key: 'general',
    label: 'General health',
    share: 20,
    patients: 505,
    abnormal: 18,
    reach: 31,
    findings: { vitd: 61, iron: 32, prediabetes: 17, thyroid: 19, ldl: 11 },
    ages: [22, 35, 28, 15],
  },
];

/* The profiles with a filter chip, in order. */
const FILTERS = PROFILES.filter((x) => x.key !== 'general');

const fmt = (n) => n.toLocaleString('en-US');

/* The "Top opportunities" tile shows filled bars, as in the photo. */
const KpiBars = ({ size }) => <BarsGlyph size={size - 2} />;

function Kpi({ icon: Icon, tone, value, label }) {
  return (
    <li className={`an-kpi an-kpi--${tone}`}>
      <span className="an-kpi__icon" aria-hidden="true">
        <Icon size={20} strokeWidth={2} aria-hidden="true" />
      </span>
      <span>
        <strong className="an-kpi__value">{value}</strong>
        <span className="an-kpi__label">{label}</span>
      </span>
    </li>
  );
}

export default function AnalyticsDashboard() {
  const [key, setKey] = useState('all');
  const p = PROFILES.find((x) => x.key === key);
  const opportunities = FINDINGS.filter((f) => p.findings[f.key] >= OPPORTUNITY_MIN).length;
  const topAge = Math.max(...p.ages);

  return (
    <div className="an-dash-stack">
      <span className="an-dash-back" aria-hidden="true">
        <span>30%</span>
        <span>22%</span>
        <span>33%</span>
      </span>
      <section className="card an-dash" aria-labelledby="an-dash-title" data-trapezium-state={`filter-${p.key}`}>
        <header className="an-dash__head">
          <h2 id="an-dash-title" className="an-dash__title">
            Patient insights
          </h2>
          <p className="an-showing" role="status">
            Showing: {p.label}
          </p>
          <p className="an-dash__dates">
            <CalendarDays size={15} aria-hidden="true" />
            Jan 2024 – Dec 2024
          </p>
        </header>

        <div className="an-chips" role="group" aria-label="Patient profile">
          {FILTERS.map((x, i) => (
            <button
              key={x.key}
              type="button"
              className={`an-chip hint${x.key === key ? ' is-active hint--active' : ''}`}
              style={{ '--hint-i': i }}
              aria-pressed={x.key === key}
              onClick={() => setKey(x.key)}
              data-trapezium-id={`${SCREEN}.filter-${x.key}`}
            >
              {x.label}
            </button>
          ))}
        </div>

        <ul className="an-kpis" aria-label={`Key figures, ${p.label}`}>
          <Kpi icon={Users} tone="blue" value={fmt(p.patients)} label="Patients in view" />
          <Kpi icon={TriangleAlert} tone="red" value={`${p.abnormal}%`} label="Have an abnormality" />
          <Kpi icon={KpiBars} tone="green" value={opportunities} label="Top opportunities" />
          <Kpi icon={TrendingUp} tone="purple" value={`+${p.reach}%`} label="Potential reach" />
        </ul>

        <div className="an-charts">
          <div className="an-panel">
            <h3 className="an-panel__title">Where opportunities are highest</h3>
            <ul className="an-hbars" aria-label={`Where opportunities are highest, ${p.label}`}>
              {FINDINGS.map((f) => (
                <li key={f.key}>
                  <span className="an-hbars__name">{f.name}</span>
                  <span className="an-hbars__track" aria-hidden="true">
                    <span style={{ width: `${p.findings[f.key]}%` }} />
                  </span>
                  <span className="an-hbars__pct">{p.findings[f.key]}%</span>
                </li>
              ))}
            </ul>
            <p className="sr-only">Share of patients tested for each marker.</p>
          </div>

          <div className="an-panel">
            <h3 className="an-panel__title">Patients by age group</h3>
            <ul className="an-vbars" aria-label={`Patients by age group, ${p.label}`}>
              {AGE_GROUPS.map((g, i) => (
                <li key={g} className={p.ages[i] === topAge ? 'is-top' : undefined}>
                  <span className="sr-only">{`Age ${g}: ${p.ages[i]}%`}</span>
                  <span className="an-vbars__pct" aria-hidden="true">
                    {p.ages[i]}%
                  </span>
                  <span className="an-vbars__col" aria-hidden="true">
                    <span style={{ height: `${Math.round((p.ages[i] / 50) * 120)}px` }} />
                  </span>
                  <span className="an-vbars__group" aria-hidden="true">
                    {g}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
